import { FC, useEffect, useRef, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import { Column } from '@material-table/core'
import moment from 'moment'

import { isDebug } from '../../configs'
import { useSessionContext, useStudentContext } from '../../contexts'

import { getModuleName } from '../../ModuleNames'
import { FaultAttemptItem, getFaultAttemptListByUser, getStudentProfileByUserID } from '../../api/LRS'

import { FaultState, SimutechModule } from '../../common/types'

import { DataTile, DataTileType, TableView } from '../dataview'
import { CriticalError, LoadingIndicator } from '../'
import { PrimaryHeader } from '../../styles/Styles'

const FaultHistory: FC = () => {
    const { currentStudent, setCurrentStudent } = useStudentContext()
    const { user } = useSessionContext()
    const history = useHistory()

    const [error, setError] = useState('')
    const [tableData, setTableData] = useState<FaultAttemptItem[]>()

    const { studentId, modId } = useParams<{ studentId: string; modId: SimutechModule }>()
    const moduleName = getModuleName(modId)

    const fetchingStudent = useRef(false)
    const fetchingHistory = useRef(false)

    useEffect(() => {
        if (!user?.lrsDbName || (currentStudent && currentStudent.userId === studentId) || fetchingStudent.current) return
        fetchingStudent.current = true
        isDebug && console.log('Fetch Student effect for Fault History studentId :', studentId)
        const fetchData = async () => {
            const student = await getStudentProfileByUserID(studentId, user.lrsDbName)
            setCurrentStudent(student)
        }
        fetchData()
            .catch(error => setError(error.toString()))

    }, [currentStudent, setCurrentStudent, studentId, user?.lrsDbName])

    useEffect(() => {
        if (!user || !currentStudent || tableData || fetchingHistory.current) return
        fetchingHistory.current = true
        const fetchData = async () => {
            const faultAttempts: FaultAttemptItem[] = await getFaultAttemptListByUser(currentStudent.userId, user.lmsSessionId, modId as SimutechModule)
            setTableData(faultAttempts)
        }
        fetchData()
            .catch(error => setError(error.toString()))

    }, [currentStudent, modId, tableData, user])

    const selectAttempt = (event: any, rowData: FaultAttemptItem | undefined) => {
        event.preventDefault()
        if (!rowData) return
        history.push(`/individual-data/${studentId}/${modId}/${rowData._id}`)
    }

    if (error) return <CriticalError message={error} />
    if (!currentStudent) return <LoadingIndicator loading={true} text={'Loading fault history...'} />

    const columns: Column<FaultAttemptItem>[] = getColumns()

    return (
        <div>
            <Grid container spacing={4} justifyContent={'flex-start'}>
                <Grid item>
                    <DataTile type={DataTileType.Employee} value={0} text={currentStudent.userName.replace(/,/g, ', ')} />
                </Grid>
            </Grid>

            <PrimaryHeader variant={'h4'}>{moduleName}</PrimaryHeader>
            <TableView
                title={'Fault Attempt History'}
                isLoading={!tableData}
                data={tableData ?? []}
                columns={columns}
                onRowClick={selectAttempt}
                paging={true}
                search={true}
                searchPlaceholder={'Search by Fault # or State'}
                sorting={true}
            />
        </div>
    )
}

export default FaultHistory

const getColumns = (): Column<FaultAttemptItem>[] => {
    return [
        {
            field: '_id',
            hidden: true,
            searchable: false
        },
        {
            title: 'Date/Time',
            field: 'timestamp',
            type: 'datetime',
            defaultSort: 'desc',
            searchable: false,
            render: (rowData) => moment(rowData.timestamp).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: 'Fault Number',
            field: 'faultId',
            type: 'string',
            searchable: true
        },
        {
            title: 'State',
            field: 'faultState',
            type: 'string',
            render: (rowData) => rowData.faultState === FaultState.NotRepaired ? 'Not Repaired' : rowData.faultState,
            searchable: true
        },
        {
            title: 'Skill Rating',
            field: 'skillRating',
            type: 'numeric',
            render: (rowData) => !rowData.skillRating ? '' : rowData.skillRating + '%',
            searchable: false
        },
    ]
}